/**
 * 2ECOMP MODIFIED FILE - Contains customizations
 *
 * Customizations:
 * - PrintHeader: Custom header for PDF/print export
 * - printStyles.css: Print-specific styling
 * - nativePostMessage: PDF generation support
 * - getExportValue: Custom Excel export formatting
 * - formControlSx/selectSx: Custom form styling
 *
 * DO NOT overwrite with upstream without merging customizations.
 * Search for "2ECOMP" comments to find all customization points.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FormControl, InputLabel, Select, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Link, IconButton,
  Box, Collapse, Typography,
} from '@mui/material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import PlaceIcon from '@mui/icons-material/Place';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from '@mui/icons-material/Timeline';
import { StatCard, StatCardGrid } from '../common/components/premium/PremiumDashboard';
import { PremiumPieChart } from '../common/components/premium/PremiumCharts';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { formatSpeed, formatTime } from '../common/util/formatter';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { prefixString, unprefixString } from '../common/util/stringUtils';
import { useTranslation, useTranslationKeys } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import usePersistedState from '../common/util/usePersistedState';
import ColumnSelect from './components/ColumnSelect';
import { useCatch, useEffectAsync } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import { useAttributePreference } from '../common/util/preferences';
import MapView from '../map/core/MapView';
import MapGeofence from '../map/MapGeofence';
import MapPositions from '../map/MapPositions';
import MapCamera from '../map/MapCamera';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import SelectField from '../common/components/SelectField';
import fetchOrThrow from '../common/util/fetchOrThrow';
import AddressValue from '../common/components/AddressValue';
import downloadReportXlsx from '../common/util/downloadReportXlsx';
// 2ECOMP START: Mini-map components
import { MiniMapCell, MapExpandModal } from '../common/components/premium';
// 2ECOMP END
// 2ECOMP START: Print/PDF support
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import { PrintLayout } from './common/print';
// 2ECOMP END

// 2ECOMP START: Custom form styling
const formControlSx = {
  minWidth: 240,
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
};

const selectSx = { backgroundColor: 'background.paper' };
// 2ECOMP END

const columnsArray = [
  ['eventTime', 'positionFixTime'],
  ['type', 'sharedType'],
  ['geofenceId', 'sharedGeofence'],
  ['maintenanceId', 'sharedMaintenance'],
  ['address', 'positionAddress'],
  ['attributes', 'commandData'],
];
const columnsMap = new Map(columnsArray);

const EventReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [searchParams, setSearchParams] = useSearchParams();

  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const geofences = useSelector((state) => state.geofences.items, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const speedUnit = useAttributePreference('speedUnit');
  const [allEventTypes, setAllEventTypes] = useState([['allEvents', 'eventAll']]);

  const alarms = useTranslationKeys((it) => it.startsWith('alarm')).map((it) => ({
    key: unprefixString('alarm', it),
    name: t(it),
  }));

  const [columns, setColumns] = usePersistedState('eventColumns', ['eventTime', 'type', 'address', 'attributes']);
  const eventTypes = useMemo(() => searchParams.getAll('eventType'), [searchParams]);
  const alarmTypes = useMemo(() => searchParams.getAll('alarmType'), [searchParams]);
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [position, setPosition] = useState(null);
  // 2ECOMP START: Print/PDF state management
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [showChart, setShowChart] = useState(true);
  const [expandedMap, setExpandedMap] = useState(null);
  // 2ECOMP END

  // Stats por tipo de evento
  const stats = useMemo(() => {
    if (items.length === 0) return null;

    const byType = {};
    items.forEach((item) => {
      const type = item.type || 'unknown';
      byType[type] = (byType[type] || 0) + 1;
    });

    const alarms = items.filter((i) => i.type === 'alarm').length;
    const geofenceEvents = items.filter((i) => i.type?.startsWith('geofence')).length;
    const overspeed = items.filter((i) => i.type === 'deviceOverspeed').length;
    const ignition = items.filter((i) => i.type === 'ignitionOn' || i.type === 'ignitionOff').length;
    const maintenance = items.filter((i) => i.type === 'maintenance').length;

    return {
      total: items.length,
      alarms,
      geofenceEvents,
      overspeed,
      ignition,
      maintenance,
      byType,
      uniqueDevices: new Set(items.map((i) => i.deviceId)).size,
    };
  }, [items]);

  // Dados para o gráfico de eventos por tipo
  const chartData = useMemo(() => {
    if (!stats?.byType) return [];
    const colorMap = {
      alarm: '#f85149',
      geofenceEnter: '#58a6ff',
      geofenceExit: '#58a6ff',
      deviceOverspeed: '#d29922',
      ignitionOn: '#3fb950',
      ignitionOff: '#6e7681',
      maintenance: '#a371f7',
      deviceOnline: '#3fb950',
      deviceOffline: '#6e7681',
    };
    return Object.entries(stats.byType).map(([type, count]) => ({
      name: t(`event${type.charAt(0).toUpperCase() + type.slice(1)}`) || type,
      value: count,
      color: colorMap[type] || '#8b949e',
    }));
  }, [stats, t]);

  // 2ECOMP START: Map event type to marker icon
  const getMarkerIcon = (eventType) => {
    switch (eventType) {
      case 'alarm':
        return 'alarm';
      case 'geofenceEnter':
        return 'enter';
      case 'geofenceExit':
        return 'exit';
      case 'deviceOverspeed':
        return 'alert';
      case 'ignitionOn':
      case 'ignitionOff':
        return 'car';
      default:
        return 'pin';
    }
  };
  // 2ECOMP END

  useEffect(() => {
    if (!eventTypes.length) {
      updateReportParams(searchParams, setSearchParams, 'eventType', ['allEvents']);
    }
  }, [searchParams, setSearchParams, eventTypes])

  useEffect(() => {
    if (selectedItem?.positionId) {
      setPosition(positions[selectedItem.positionId] || null);
    } else {
      setPosition(null);
    }
  }, [selectedItem, positions]);

useEffectAsync(async () => {
  const response = await fetchOrThrow('/api/notifications/types');
  const types = await response.json();
  setAllEventTypes([...allEventTypes, ...types.map((it) => [it.type, prefixString('event', it.type)])]);
}, []);

// 2ECOMP START: Print/PDF event handlers
useEffect(() => {
  const handleAfterPrint = () => {
    dispatch(sessionActions.setPrinting(false));
  };
  window.addEventListener('afterprint', handleAfterPrint);
  return () => {
    window.removeEventListener('afterprint', handleAfterPrint);
  };
}, [dispatch]);

useEffect(() => {
  if (printing) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => nativePostMessage(`print|${t('reportEvents')}.pdf`)),
    );
  }
}, [printing, t]);
// 2ECOMP END

  const loadEvents = async ({ deviceIds = [], groupIds = [], from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    eventTypes.forEach((it) => query.append('type', it));
    if (eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm')) {
      alarmTypes.forEach((it) => query.append('alarm', it));
    }
    setSelectedItem(null);
    setPosition(null);
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/events?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const events = await response.json();
      setItems(events);
      setHasResults(events.length > 0);
      const positionIds = Array.from(new Set(events
        .map((event) => event.positionId)
        .filter((id) => id)));
      const positionsMap = {};
      if (positionIds.length > 0) {
        const positionsQuery = new URLSearchParams();
        positionIds.slice(0, 128).forEach((id) => positionsQuery.append('id', id));
        const positionsResponse = await fetchOrThrow(`/api/positions?${positionsQuery.toString()}`);
        const positionsArray = await positionsResponse.json();
        positionsArray.forEach((p) => positionsMap[p.id] = p);
      }
      setPositions(positionsMap);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setSelectedGroupIds(groupIds);
    setFromDate(from || '');
    setToDate(to || '');
    await loadEvents({ deviceIds, groupIds, from, to });
  });

  const onExport = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((id) => query.append('deviceId', id));
    groupIds.forEach((id) => query.append('groupId', id));
    eventTypes.forEach((it) => query.append('type', it));
    if (eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm')) {
      alarmTypes.forEach((it) => query.append('alarm', it));
    }
    await downloadReportXlsx('events', query, `${t('reportEvents')}.xlsx`);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'events';
    if (eventTypes[0] !== 'allEvents') {
      report.attributes.types = eventTypes.join(',');
    }
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  // 2ECOMP START: Print handler with data preloading
  const onPrint = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveGroupIds = groupIds.length ? groupIds : selectedGroupIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!items.length) {
      await loadEvents({
        deviceIds: effectiveDeviceIds,
        groupIds: effectiveGroupIds,
        from: effectiveFrom,
        to: effectiveTo,
      });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setSelectedGroupIds(effectiveGroupIds);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    dispatch(sessionActions.setPrinting(true));
  });
  // 2ECOMP END

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value].name;
      case 'eventTime':
        return formatTime(value, 'seconds');
      case 'type':
        return t(prefixString('event', value));
      case 'geofenceId':
        if (value > 0) {
          const geofence = geofences[value];
          return geofence && geofence.name;
        }
        return null;
      case 'maintenanceId':
        return value > 0 ? value : null;
      case 'address': {
        const position = positions[item.positionId];
        if (position) {
          return (
            <AddressValue
              latitude={position.latitude}
              longitude={position.longitude}
              originalAddress={position.address}
            />
          );
        }
        return '';
      }
      case 'attributes':
        switch (item.type) {
          case 'alarm':
            return t(prefixString('alarm', item.attributes.alarm));
          case 'deviceOverspeed':
            return formatSpeed(item.attributes.speed, speedUnit, t);
          case 'driverChanged':
            return item.attributes.driverUniqueId;
          case 'media':
            return (<Link href={`/api/media/${devices[item.deviceId]?.uniqueId}/${item.attributes.file}`} target="_blank">{item.attributes.file}</Link>);
          case 'commandResult':
            return item.attributes.result;
          default:
            return '';
        }
      default:
        return value;
    }
  };

  // 2ECOMP START: Printable value formatter (returns plain text for PDF)
  const getPrintableValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || `${value}`;
      case 'eventTime':
        return formatTime(value, 'seconds');
      case 'type':
        return t(prefixString('event', value));
      case 'geofenceId': {
        if (value > 0) {
          const geofence = geofences[value];
          return geofence?.name || `${value}`;
        }
        return '';
      }
      case 'maintenanceId':
        return value > 0 ? `${value}` : '';
      case 'address': {
        const source = positions[item.positionId];
        return source?.address || '';
      }
      case 'attributes':
        switch (item.type) {
          case 'alarm':
            return t(prefixString('alarm', item.attributes.alarm));
          case 'deviceOverspeed':
            return formatSpeed(item.attributes.speed, speedUnit, t);
          case 'driverChanged':
            return item.attributes.driverUniqueId || '';
          case 'media':
            return item.attributes.file || '';
          case 'commandResult':
            return item.attributes.result || '';
          default:
            return '';
        }
      default:
        return value != null ? `${value}` : '';
    }
  };
  // 2ECOMP END

  // 2ECOMP START: Print/PDF render mode
  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices[deviceId]?.name || devices[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  if (printing) {
    return (
      <PrintLayout
        title={t('reportEvents')}
        from={fromDate}
        to={toDate}
        deviceName={deviceNames}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('sharedDevice')}</TableCell>
              {/* 2ECOMP: Mini-map column for print */}
              <TableCell sx={{ width: 100, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`print-${item.id}`}>
                <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                {/* 2ECOMP: Mini-map cell for print */}
                <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                  {positions[item.positionId] ? (
                    <MiniMapCell
                      latitude={positions[item.positionId].latitude}
                      longitude={positions[item.positionId].longitude}
                      size="xs"
                      markerIcon={getMarkerIcon(item.type)}
                      showExpandButton={false}
                      forPrint
                    />
                  ) : null}
                </TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{getPrintableValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }
  // 2ECOMP END

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportEvents']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              {position && <MapPositions positions={[position]} titleField="fixTime" />}
            </MapView>
            <MapScale />
            {position && <MapCamera latitude={position.latitude} longitude={position.longitude} />}
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onPrint={onPrint}
              onSchedule={onSchedule}
              deviceType="multiple"
              loading={loading}
              hasResults={hasResults}
            >
              <div className={classes.filterItem}>
                <FormControl fullWidth size="small" variant="outlined" sx={formControlSx}>
                  <InputLabel>{t('reportEventTypes')}</InputLabel>
                  <Select
                    label={t('reportEventTypes')}
                    value={eventTypes}
                    onChange={(e, child) => {
                      let values = e.target.value;
                      const clicked = child.props.value;
                      if (values.includes('allEvents') && values.length > 1) {
                        values = [clicked];
                      }
                      updateReportParams(searchParams, setSearchParams, 'eventType', values)
                    }}
                    multiple
                    size="small"
                    sx={selectSx}
                  >
                    {allEventTypes.map(([key, string]) => (
                      <MenuItem key={key} value={key}>{t(string)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm') && (
                <div className={classes.filterItem}>
                  <SelectField
                    multiple
                    value={alarmTypes}
                    onChange={(e) => updateReportParams(searchParams, setSearchParams, 'alarmType', e.target.value)}
                    data={alarms}
                    keyGetter={(it) => it.key}
                    label={t('sharedAlarms')}
                    fullWidth
                  />
                </div>
              )}
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
            </ReportFilter>
          </div>
          {/* Premium Stats Cards */}
          {stats && (
            <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}` }}>
              <StatCardGrid columns={5} gap={2}>
                <StatCard
                  title={t('reportEvents')}
                  value={stats.total}
                  icon={<NotificationsActiveIcon />}
                  color="primary"
                  size="sm"
                  subtitle={`${stats.uniqueDevices} ${t('deviceTitle')}`}
                />
                {stats.alarms > 0 && (
                  <StatCard
                    title={t('sharedAlarms')}
                    value={stats.alarms}
                    icon={<WarningAmberIcon />}
                    color="error"
                    size="sm"
                  />
                )}
                {stats.overspeed > 0 && (
                  <StatCard
                    title={t('eventDeviceOverspeed')}
                    value={stats.overspeed}
                    icon={<SpeedIcon />}
                    color="warning"
                    size="sm"
                  />
                )}
                {stats.geofenceEvents > 0 && (
                  <StatCard
                    title={t('sharedGeofences')}
                    value={stats.geofenceEvents}
                    icon={<PlaceIcon />}
                    color="info"
                    size="sm"
                  />
                )}
                {stats.ignition > 0 && (
                  <StatCard
                    title={t('positionIgnition')}
                    value={stats.ignition}
                    icon={<PowerSettingsNewIcon />}
                    size="sm"
                  />
                )}
              </StatCardGrid>

              {/* Chart Toggle */}
              {chartData.length > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    onClick={() => setShowChart(!showChart)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: tokens?.text?.secondary || '#8b949e',
                      '&:hover': { color: tokens?.accent?.primary || '#00d4aa' },
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('reportChart') || 'Gráfico'}
                    </Typography>
                    {showChart ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </Box>
                  <Collapse in={showChart}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: tokens?.surface?.elevated || '#161b22',
                        borderRadius: tokens?.radius?.lg || '12px',
                        border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, color: tokens?.text?.secondary || '#8b949e' }}
                      >
                        {t('reportEventTypes') || 'Eventos por Tipo'}
                      </Typography>
                      <PremiumPieChart
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        height={200}
                        innerRadius={50}
                        showLabels
                        tooltipFormatter={(value) => `${value} ${t('reportEvents')}`}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>{t('sharedDevice')}</TableCell>
                {/* 2ECOMP: Mini-map column */}
                <TableCell sx={{ width: 140, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
                {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.columnAction} padding="none">
                    {(item.positionId && (selectedItem === item ? (
                      <IconButton size="small" onClick={() => setSelectedItem(null)}>
                        <GpsFixedIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => setSelectedItem(item)}>
                        <LocationSearchingIcon fontSize="small" />
                      </IconButton>
                    ))) || ''}
                  </TableCell>
                  <TableCell>{devices[item.deviceId].name}</TableCell>
                  {/* 2ECOMP: Mini-map cell */}
                  <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                    {positions[item.positionId] ? (
                      <MiniMapCell
                        latitude={positions[item.positionId].latitude}
                        longitude={positions[item.positionId].longitude}
                        size="sm"
                        markerIcon={getMarkerIcon(item.type)}
                        onClick={() => setExpandedMap({
                          latitude: positions[item.positionId].latitude,
                          longitude: positions[item.positionId].longitude,
                          title: t(prefixString('event', item.type)),
                          subtitle: formatTime(item.eventTime, 'seconds'),
                          deviceName: devices[item.deviceId]?.name,
                          eventType: item.type,
                          geofenceId: item.geofenceId,
                          geofenceName: item.geofenceId > 0 ? geofences[item.geofenceId]?.name : null,
                        })}
                        tooltipTitle={t('mapExpand') || 'Expandir mapa'}
                      />
                    ) : (
                      <Box sx={{ width: 80, height: 60 }} />
                    )}
                  </TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      {formatValue(item, key)}
                    </TableCell>
                  ))}
                </TableRow>
              )) : (<TableShimmer columns={columns.length + 3} />)}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* 2ECOMP: Expanded map modal */}
      <MapExpandModal
        open={!!expandedMap}
        onClose={() => setExpandedMap(null)}
        latitude={expandedMap?.latitude}
        longitude={expandedMap?.longitude}
        title={expandedMap?.title}
        subtitle={expandedMap?.subtitle}
        deviceName={expandedMap?.deviceName}
        eventType={expandedMap?.eventType}
        geofenceId={expandedMap?.geofenceId}
        geofenceName={expandedMap?.geofenceName}
        markerCategory={expandedMap?.eventType === 'alarm' ? 'alarm' :
          expandedMap?.eventType === 'geofenceEnter' ? 'geofenceEnter' :
          expandedMap?.eventType === 'geofenceExit' ? 'geofenceExit' : 'default'}
      />
    </PageLayout>
  );
};

export default EventReportPage;
